import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://velor.ae";
const DEFAULT_OG = `${SITE}/favicon.png`;

/**
 * Reusable SEO component for every page.
 *
 * @param {string} title         — Page-specific title (suffixed with " · Velor Advisory")
 * @param {string} description   — Meta description (max ~155 chars)
 * @param {string} keywords      — Comma-separated keywords
 * @param {string} [focusKeyword]— Primary focus keyword for the page
 * @param {string} path          — Path starting with '/' for canonical + og:url
 * @param {string} [image]       — Optional OG image URL
 * @param {object|array} [schema]— JSON-LD schema object (or array of objects)
 * @param {boolean} [noindex]    — Set true to prevent indexing
 */
export default function Seo({
  title,
  description,
  keywords,
  focusKeyword,
  path = "/",
  image = DEFAULT_OG,
  schema,
  noindex = false,
}) {
  const fullTitle = title ? `${title} · Velor Advisory` : "Velor Advisory · British Advisory in the UAE";
  const url = `${SITE}${path}`;

  // Inject JSON-LD schema into <head>. React 19's tag hoisting does not
  // hoist inline <script> tags, and react-helmet-async passes them through
  // to <body>. We inject them manually with a unique data-attribute so they
  // can be cleaned up on route change.
  useEffect(() => {
    if (!schema) return;
    const arr = Array.isArray(schema) ? schema : [schema];
    const nodes = arr.map((s) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-seo", "jsonld");
      el.text = JSON.stringify(s);
      document.head.appendChild(el);
      return el;
    });
    return () => {
      nodes.forEach((n) => n.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(schema)]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {focusKeyword && <meta name="focus-keyword" content={focusKeyword} />}
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1"}
      />
      <meta name="author" content="Velor Advisory" />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Velor Advisory" />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared schema builders                                             */
/* ------------------------------------------------------------------ */

export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE}/#organization`,
  name: "Velor Advisory",
  legalName: "Velor Advisory FZ-LLC",
  url: SITE,
  logo: `${SITE}/favicon.png`,
  image: `${SITE}/favicon.png`,
  description:
    "British-owned UAE consultancy delivering Golden Visas, residency, business setup and concierge relocation across the Emirates.",
  foundingDate: "2023",
  areaServed: [
    { "@type": "Country", name: "United Arab Emirates" },
    { "@type": "Country", name: "United Kingdom" },
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Dubai",
    addressCountry: "AE",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "contact@velor.ae",
      availableLanguage: ["English"],
      areaServed: ["AE", "GB", "EU"],
    },
  ],
  sameAs: [
    "https://www.linkedin.com/company/velor-advisory",
    "https://www.instagram.com/veloradvisory",
  ],
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE}/#website`,
  url: SITE,
  name: "Velor Advisory",
  publisher: { "@id": `${SITE}/#organization` },
  inLanguage: "en-GB",
};

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE}${it.path}`,
    })),
  };
}

export function serviceSchema({ name, description, path, serviceType, offers }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE}${path}#service`,
    name,
    description,
    serviceType,
    provider: { "@id": `${SITE}/#organization` },
    areaServed: [{ "@type": "Country", name: "United Arab Emirates" }],
    url: `${SITE}${path}`,
    ...(offers ? { hasOfferCatalog: offers } : {}),
  };
}

export function faqSchema(items = []) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((q) => ({
      "@type": "Question",
      name: q.q,
      acceptedAnswer: { "@type": "Answer", text: q.a },
    })),
  };
}
